// File: src/pages/AnalyticsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { fetchAllTests } from "../api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseISO, format, addDays } from "date-fns";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

export default function AnalyticsPage({ user }) {
  const [loading, setLoading] = useState(true);
  const [rawDailyMap, setRawDailyMap] = useState({}); // { 'YYYY-MM-DD': { date, attempted, correct, wrong } }
  const [startDateStr, setStartDateStr] = useState(""); // 'YYYY-MM-DD' or ''
  const [endDateStr, setEndDateStr] = useState(""); // 'YYYY-MM-DD' or ''
  useEffect(() => {
    // helper: produce 'YYYY-MM-DD' using local date parts (avoids UTC shift)
    function formatLocalDate(d) {
      if (!(d instanceof Date)) d = new Date(d);
      if (Number.isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }

    let mounted = true;
    async function load() {
      if (!user) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const tests = await fetchAllTests();
        if (!mounted) return;

        const daily = {}; // map dateKey -> counters

        for (const test of tests || []) {
          if (!test || !test.results) continue;
          const res = test.results[user.uid];
          if (!res) continue;

          // submittedAt possibly Firestore Timestamp or Date/string
          let submittedAt = null;
          if (res.submittedAt) {
            if (
              res.submittedAt.seconds &&
              typeof res.submittedAt.seconds === "number"
            ) {
              submittedAt = new Date(res.submittedAt.seconds * 1000);
            } else {
              submittedAt = new Date(res.submittedAt);
            }
          } else {
            // no timestamp — skip (can't attribute to a day)
            continue;
          }
          if (Number.isNaN(submittedAt.getTime())) continue;

          // IMPORTANT: use local date (year-month-day) so local-day submissions aren't shifted back by UTC
          const dateKey = formatLocalDate(submittedAt); // e.g. "2025-09-27"

          if (!dateKey) continue;

          if (!daily[dateKey])
            daily[dateKey] = {
              date: dateKey,
              attempted: 0,
              correct: 0,
              wrong: 0,
            };

          const qDetails = Array.isArray(res.questionDetails)
            ? res.questionDetails
            : [];

          for (const q of qDetails) {
            // attempted: use explicit flag if present, otherwise infer from userAnswer
            const attempted =
              !!q.attempted ||
              (q.userAnswer !== undefined &&
                q.userAnswer !== null &&
                String(q.userAnswer).trim() !== "");
            const correct = !!q.correct;
            if (attempted) daily[dateKey].attempted += 1;
            if (attempted && correct) daily[dateKey].correct += 1;
            if (attempted && !correct) daily[dateKey].wrong += 1;
          }
        }

        // sort date keys ascending
        const keys = Object.keys(daily).sort((a, b) => a.localeCompare(b));
        // build sorted map (object) to store in state
        const sortedMap = {};
        keys.forEach((k) => (sortedMap[k] = daily[k]));
        setRawDailyMap(sortedMap);

        // initialize start/end defaults to min/max dates that have data
        if (keys.length > 0) {
          setStartDateStr(keys[0]);
          setEndDateStr(keys[keys.length - 1]);
        } else {
          setStartDateStr("");
          setEndDateStr("");
        }
      } catch (e) {
        console.error("Failed to fetch analytics data:", e);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [user]);

  // derive an ordered array (filling missing dates between min & max with zeros)
  // fixed: use local-date formatting (getFullYear/getMonth/getDate) instead of toISOString()
  // which avoids UTC shift that moves dates back for IST (UTC+5:30)
  const series = useMemo(() => {
    const keys = Object.keys(rawDailyMap || {});
    if (!keys.length) return [];

    const minKey = keys[0];
    const maxKey = keys[keys.length - 1];
    const out = [];

    let cur = parseISO(minKey);
    const end = parseISO(maxKey);

    const pad = (n) => String(n).padStart(2, "0");
    const formatLocalDate = (d) =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    // iterate day-by-day using date-fns addDays, but format keys using local date parts
    while (cur <= end) {
      const key = formatLocalDate(cur); // local YYYY-MM-DD matching how keys were produced
      if (rawDailyMap[key]) out.push({ ...rawDailyMap[key] });
      else out.push({ date: key, attempted: 0, correct: 0, wrong: 0 });
      cur = addDays(cur, 1);
    }

    return out;
  }, [rawDailyMap]);

  // filtered series for chart based on start/end selection
  const filteredSeries = useMemo(() => {
    if (!series || series.length === 0) return [];
    // ensure startDateStr/endDateStr are within series range; if empty use series min/max
    const minDate = series[0].date;
    const maxDate = series[series.length - 1].date;
    const start =
      startDateStr && startDateStr >= minDate ? startDateStr : minDate;
    const end = endDateStr && endDateStr <= maxDate ? endDateStr : maxDate;
    // slice between start and end inclusive
    return series.filter((d) => d.date >= start && d.date <= end);
  }, [series, startDateStr, endDateStr]);

  // totals for the currently filtered window
  const totals = useMemo(() => {
    return filteredSeries.reduce(
      (acc, d) => {
        acc.attempted += d.attempted || 0;
        acc.correct += d.correct || 0;
        acc.wrong += d.wrong || 0;
        return acc;
      },
      { attempted: 0, correct: 0, wrong: 0 }
    );
  }, [filteredSeries]);

  // datepicker helpers: convert YYYY-MM-DD string to Date object for DatePicker selected prop
  const toDate = (s) => (s ? parseISO(s) : null);
  const fromDateToStr = (d) => (d ? format(d, "yyyy-MM-dd") : "");

  // min/max bounds for pickers (based on series)
  const minAvailable = series.length ? series[0].date : null;
  const maxAvailable = series.length ? series[series.length - 1].date : null;

  if (!user) {
    return (
      <div className="p-6">
        <div className="text-lg text-red-600">
          Please log in to view analytics.
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="p-6">Loading analytics…</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Study Analytics</h1>
        <div className="text-sm text-gray-600">
          Data derived from your submitted tests
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm mb-1">Start date</label>
          <DatePicker
            selected={toDate(startDateStr)}
            onChange={(d) => setStartDateStr(fromDateToStr(d))}
            minDate={minAvailable ? parseISO(minAvailable) : undefined}
            maxDate={maxAvailable ? parseISO(maxAvailable) : undefined}
            dateFormat="yyyy-MM-dd"
            placeholderText={minAvailable || "No data"}
            className="border rounded px-2 py-1"
            isClearable
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </div>

        <div>
          <label className="block text-sm mb-1">End date</label>
          <DatePicker
            selected={toDate(endDateStr)}
            onChange={(d) => setEndDateStr(fromDateToStr(d))}
            minDate={minAvailable ? parseISO(minAvailable) : undefined}
            maxDate={maxAvailable ? parseISO(maxAvailable) : undefined}
            dateFormat="yyyy-MM-dd"
            placeholderText={maxAvailable || "No data"}
            className="border rounded px-2 py-1"
            isClearable
            showMonthDropdown
            showYearDropdown
            dropdownMode="select"
          />
        </div>

        <div>
          <button
            onClick={() => {
              // reset to available min/max
              setStartDateStr(minAvailable || "");
              setEndDateStr(maxAvailable || "");
            }}
            className="px-3 py-1 border rounded text-sm"
          >
            Reset range
          </button>
        </div>

        <div className="ml-4 text-sm text-gray-600">
          {series && series.length > 0 ? (
            <>
              Data available:{" "}
              <strong>{format(parseISO(series[0].date), "dd MMM yyyy")}</strong>{" "}
              —{" "}
              <strong>
                {format(
                  parseISO(series[series.length - 1].date),
                  "dd MMM yyyy"
                )}
              </strong>
            </>
          ) : (
            <span>No data available</span>
          )}
        </div>
      </div>

      <div style={{ height: 420 }}>
        {filteredSeries.length === 0 ? (
          <div className="text-gray-600">No data in the selected range.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={filteredSeries}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                type="monotone"
                dataKey="attempted"
                stroke="#5b21b6"
                strokeWidth={2}
                name="Attempted"
              />
              <Line
                type="monotone"
                dataKey="correct"
                stroke="#16a34a"
                strokeWidth={2}
                name="Correct"
              />
              <Line
                type="monotone"
                dataKey="wrong"
                stroke="#ef4444"
                strokeWidth={2}
                name="Wrong"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-6 text-sm text-gray-700 space-y-1">
        <div>Total days shown: {filteredSeries.length}</div>
        <div>Total attempted: {totals.attempted}</div>
        <div>Total correct: {totals.correct}</div>
        <div>Total wrong: {totals.wrong}</div>
      </div>
    </div>
  );
}
